import { defineStore } from 'pinia';
import { api } from '../services/apiClient';
import { useAuthStore } from './authStore';

export const useOrganizationStore = defineStore('organization', {
  state: () => ({
    organizations: [],
    users: [], // Organization Members
    teams: [], // Organization Teams (Groups)
    activeTeamMembers: [], // Members of the currently selected team
  }),
  actions: {
    // --- Organization Actions ---

    async fetchOrganizations() {
      try {
        const res = await api.get('/users/me/organizations');
        this.organizations = res.data.organizations || res.data.items || res.data || [];
        const authStore = useAuthStore();
        await authStore.syncOrganizations(this.organizations);
      } catch (err) {
        console.error("Fetch failed", err);
      }
    },

    async createOrganization(name, subscription) {
      const res = await api.post('/users/me/organizations', {
        organization: { name: name, subscription: subscription }
      });
      const newOrg = res.data.organization || res.data;

      if (Array.isArray(this.organizations)) {
        this.organizations.push(newOrg);
      } else {
        this.organizations = [newOrg];
      }

      const authStore = useAuthStore();
      if (authStore.organizations) {
        await authStore.syncOrganizations([...authStore.organizations, newOrg]);
      }
      location.href = newOrg.subscription.sessionURL;
      return newOrg;
    },

    async updateOrganization(id, name) {
      const res = await api.patch(`/organizations/${id}`, {
        organization: { name }
      });
      const updatedFields = res.data.organization || res.data;

      const index = this.organizations.findIndex(o => o.id === id);
      if (index !== -1) {
        this.organizations[index] = { ...this.organizations[index], ...updatedFields };
      }

      const authStore = useAuthStore();
      if (authStore.organizations) {
        const newOrgs = authStore.organizations.map(o =>
          o.id === id ? { ...o, ...updatedFields } : o
        );
        await authStore.syncOrganizations(newOrgs);
      }
      return updatedFields;
    },

    async deleteOrganization(id) {
      await api.delete(`/organizations/${id}`);
      this.organizations = this.organizations.filter(o => o.id !== id);

      const authStore = useAuthStore();
      if (authStore.organizations) {
        const newOrgs = authStore.organizations.filter(o => o.id !== id);
        await authStore.syncOrganizations(newOrgs);
      }

      if (authStore.activeOrganization?.id === id) {
        authStore.setActiveOrganization(null);
      }
    },

    // --- API Key Actions ---

    async createApiKey(organizationId, payload) {
      const res = await api.post(`/organizations/${organizationId}/keys`, payload);
      return res.data.key || res.data;
    },

    // --- OAuth Application Actions ---

    async listOAuthApplications(organizationId) {
      const res = await api.get(`/organizations/${organizationId}/oauth-applications`);
      return res.data.applications || [];
    },

    async createOAuthApplication(organizationId, payload) {
      const res = await api.post(`/organizations/${organizationId}/oauth-applications`, payload);
      return res.data;
    },

    async deleteOAuthApplication(organizationId, appId) {
      await api.delete(`/organizations/${organizationId}/oauth-applications/${appId}`);
    },

    // --- Subscription Actions ---

    async createSubscriptionSession(organizationId, plan) {
      const res = await api.post(`/organizations/${organizationId}/subscriptions/sessions`, {
        subscription: { plan }
      });
      return res.data.subscription;
    },

    async syncSubscription(organizationId) {
      try {
        await api.post(`/organizations/${organizationId}/subscriptions/syncs`, {
          subscription: {}
        });
        await this.fetchOrganizations();
      } catch (err) {
        console.error("Fetch failed", err);
      }
    },

    // --- Organization Member Actions ---

    async fetchUsers(organizationId) {
      const res = await api.get(`/organizations/${organizationId}/users`);
      this.users = res.data.users || [];
      return this.users;
    },

    async addUser(organizationId, memberData) {
      const payload = { user: memberData };
      const res = await api.post(`/organizations/${organizationId}/users`, payload);
      const newMember = res.data.user || res.data;
      this.users.push(newMember);
      return newMember;
    },

    async removeUser(organizationId, memberId) {
      await api.delete(`/organizations/${organizationId}/users/${memberId}`);
      this.users = this.users.filter(m => m.id !== memberId);
    },

    // --- Team (Group) Management Actions ---

    async fetchTeams(organizationId) {
      const res = await api.get(`/organizations/${organizationId}/groups`);
      this.teams = res.data.groups || [];
      return this.teams;
    },

    async createTeam(organizationId, name) {
      const res = await api.post(`/organizations/${organizationId}/groups`, {
        group: { name }
      });
      const newTeam = res.data.group || res.data;
      this.teams.push(newTeam);
      return newTeam;
    },

    async updateTeam(organizationId, teamId, name) {
      const res = await api.patch(`/organizations/${organizationId}/groups/${teamId}`, {
        group: { name }
      });
      const updatedTeam = res.data.group || res.data;

      const index = this.teams.findIndex(t => t.id === teamId);
      if (index !== -1) {
        this.teams[index] = { ...this.teams[index], ...updatedTeam };
      }
      return updatedTeam;
    },

    async deleteTeam(organizationId, teamId) {
      await api.delete(`/organizations/${organizationId}/groups/${teamId}`);
      this.teams = this.teams.filter(t => t.id !== teamId);
    },

    // --- Team Member Actions ---

    async fetchTeamMembers(organizationId, teamId) {
      const res = await api.get(`/organizations/${organizationId}/groups/${teamId}/members`);
      this.activeTeamMembers = res.data.members || [];
      return this.activeTeamMembers;
    },

    async addTeamMember(organizationId, teamId, userId, role = 'group_member') {
      const payload = {
        member: {
          id: userId,
          type: 'user',
          meta: { assignRole: role }
        }
      };

      const res = await api.post(`/organizations/${organizationId}/groups/${teamId}/members`, payload);
      const newMember = res.data.member || res.data;

      this.activeTeamMembers.push(newMember);
      return newMember;
    },

    async removeTeamMember(organizationId, teamId, memberId) {
      const resourceId = `user-${memberId}`;
      await api.delete(`/organizations/${organizationId}/groups/${teamId}/members/${resourceId}`);
      this.activeTeamMembers = this.activeTeamMembers.filter(m => m.id !== memberId);
    },

    // --- Helpers ---

    getOrganizationById(id) {
      return this.organizations.find(o => o.id === id);
    },

    getTeamById(id) {
      return this.teams.find(t => t.id === id);
    }
  }
});